import './landing.css';
import { Nav } from './Nav';
import type { AppPage } from './LandingPage';

interface TermsPageProps {
  onNavigate: (page: AppPage) => void;
  onGetStarted: () => void;
  onLogin: () => void;
  isAuthenticated?: boolean;
}

export function TermsPage({ onNavigate, onGetStarted, onLogin, isAuthenticated }: TermsPageProps) {
  return (
    <div className="legal-page">
      <Nav currentPage="terms" onNavigate={onNavigate} onGetStarted={onGetStarted} onLogin={onLogin} isAuthenticated={isAuthenticated} />

      <div className="legal-body">
        <p className="eyebrow legal-eyebrow">Legal</p>
        <h1 className="legal-h1">Terms of Service</h1>
        <p className="legal-date">Last updated: January 15, 2025</p>

        <div className="legal-content">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By creating an account, installing the Salifly Chrome extension, or accessing the Salifly
              web website (collectively, the "Service"), you agree to be bound by these Terms of Service
              ("Terms"). If you are using the Service on behalf of an organization, you represent that you
              have authority to bind that organization to these Terms.
            </p>
            <p>
              If you do not agree to these Terms, do not use the Service. We may update these Terms from
              time to time. Continued use of the Service after changes are posted constitutes acceptance.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              Salifly is an intelligent sales enablement tool delivered as a Chrome browser extension and
              accompanying web website. The Service reads conversation threads from your active browser
              tab, extracts lead context through automated analysis, and generates suggested
              outreach message drafts. The Service is designed for use in legitimate B2B sales and business
              development activities.
            </p>
            <p>
              Salifly is provided on an "as is" basis. Features, pricing, and availability may change at
              any time with reasonable notice.
            </p>
          </section>

          <section>
            <h2>3. Account Registration</h2>
            <p>
              To use Salifly, you must create an account using a valid email address or Google OAuth. You are
              responsible for maintaining the security of your account credentials and for all activities
              that occur under your account. Notify us immediately at{' '}
              <a href="mailto:hello@salifly.com">hello@salifly.com</a> if you believe your account has been
              compromised.
            </p>
            <p>
              You must be at least 16 years old to use the Service. By creating an account, you represent
              that you meet this requirement.
            </p>
          </section>

          <section>
            <h2>4. Subscriptions and Payment</h2>
            <p>
              Salifly offers a free Starter tier and paid Basic, Pro, and Team plans billed monthly or annually. By
              subscribing to a paid plan, you authorize us to charge your payment method on a recurring
              basis until you cancel.
            </p>
            <ul>
              <li>
                <strong>Cancellation.</strong> You may cancel your subscription at any time from your account
                settings. Cancellation takes effect at the end of the current billing period. We do not
                provide refunds for partial billing periods except as required by applicable law.
              </li>
              <li>
                <strong>Price changes.</strong> We will provide at least 30 days' notice before changing
                subscription pricing. Continued use after the effective date of a price change constitutes
                acceptance of the new pricing.
              </li>
              <li>
                <strong>Taxes.</strong> All prices are exclusive of applicable taxes. You are responsible
                for any taxes associated with your use of the Service.
              </li>
            </ul>
          </section>

          <section>
            <h2>5. Permitted Use</h2>
            <p>
              You may use Salifly solely for lawful business purposes, including:
            </p>
            <ul>
              <li>B2B sales outreach, follow-ups, and deal management</li>
              <li>Business development and partnership communications</li>
              <li>Customer success and account management</li>
              <li>Recruiting outreach conducted in compliance with applicable law</li>
            </ul>
            <p>
              You agree to use Salifly-generated drafts as a starting point and to review, edit, and take
              responsibility for any messages you send. You represent that you have the right to analyze
              the conversation threads you submit to the Service.
            </p>
          </section>

          <section>
            <h2>6. Prohibited Use</h2>
            <p>You may not use the Service to:</p>
            <ul>
              <li>Send spam, unsolicited bulk messages, or messages that violate CAN-SPAM, CASL, or similar laws</li>
              <li>Harass, threaten, or abuse any individual</li>
              <li>Generate content that is discriminatory, defamatory, or illegal</li>
              <li>Scrape or harvest data from platforms in violation of those platforms' terms of service</li>
              <li>Impersonate another person or entity</li>
              <li>Circumvent technical measures designed to protect the Service or third-party platforms</li>
              <li>Attempt to reverse-engineer, decompile, or extract source code from the Service</li>
              <li>Resell, sublicense, or otherwise commercially exploit the Service without written authorization</li>
              <li>Use the Service in any way that violates applicable local, state, national, or international law</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that violate these restrictions without
              notice or refund.
            </p>
          </section>

          <section>
            <h2>7. Generated Content</h2>
            <p>
              The message drafts, rationale, and suggested next steps generated by Salifly are produced
              by automated systems. You acknowledge that:
            </p>
            <ul>
              <li>
                Generated content may be inaccurate, incomplete, or inappropriate for your specific
                situation. You are solely responsible for reviewing drafts before sending them.
              </li>
              <li>
                Salifly does not guarantee that generated messages will achieve any particular sales
                outcome or comply with all applicable laws and regulations.
              </li>
              <li>
                You retain ownership of and responsibility for all messages you send, including those based
                on Salifly-generated drafts.
              </li>
              <li>
                We do not use your conversation data or generated content to train any models.
              </li>
            </ul>
          </section>

          <section>
            <h2>8. Intellectual Property</h2>
            <p>
              Salifly and its original content, features, and functionality (including the extension code,
              website UI, automation logic, and playbook rules) are owned by Salifly and protected by
              applicable intellectual property laws.
            </p>
            <p>
              You retain all rights to content you create, including messages you send based on Salifly-generated
              drafts. By submitting conversation data to the Service, you grant us a limited, non-exclusive
              license to process that data solely for the purpose of providing the Service to you.
            </p>
          </section>

          <section>
            <h2>9. Privacy</h2>
            <p>
              Your use of the Service is subject to our{' '}
              <button className="legal-inline-link" onClick={() => onNavigate('privacy')}>Privacy Policy</button>
              , which is incorporated into these Terms by reference. By using the Service, you consent to
              the data practices described in the Privacy Policy.
            </p>
          </section>

          <section>
            <h2>10. Third-Party Services and Platforms</h2>
            <p>
              Salifly operates within and alongside third-party platforms (Gmail, LinkedIn, Salesforce, etc.).
              Your use of those platforms is governed by their own terms of service. Salifly is not affiliated
              with, endorsed by, or sponsored by any third-party platform mentioned in our marketing materials
              or documentation.
            </p>
            <p>
              We are not responsible for any changes to third-party platforms or their terms that may affect
              the functionality of the Service.
            </p>
          </section>

          <section>
            <h2>11. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS
              OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES
              OR OTHER HARMFUL COMPONENTS. WE DO NOT GUARANTEE THE ACCURACY OR COMPLETENESS OF ANY
              GENERATED CONTENT.
            </p>
          </section>

          <section>
            <h2>12. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, SALIFLY AND ITS OFFICERS, DIRECTORS,
              EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
              OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, INCLUDING BUT NOT
              LIMITED TO LOST PROFITS, LOST REVENUE, LOSS OF DATA, OR BUSINESS INTERRUPTION.
            </p>
            <p>
              IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID
              TO US IN THE 12 MONTHS PRECEDING THE CLAIM, OR (B) $100 USD.
            </p>
          </section>

          <section>
            <h2>13. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Salifly and its affiliates, officers,
              directors, employees, and agents from and against any claims, liabilities, damages, losses,
              and expenses (including reasonable attorneys' fees) arising out of or related to: (a) your
              use of the Service; (b) your violation of these Terms; (c) your violation of any third
              party's rights; or (d) any content you transmit through the Service.
            </p>
          </section>

          <section>
            <h2>14. Term and Termination</h2>
            <p>
              These Terms are effective from the date you first use the Service and remain in effect until
              terminated. You may terminate your account at any time by deleting it from account settings.
            </p>
            <p>
              We may suspend or terminate your access to the Service immediately, without notice or
              liability, if you breach these Terms or if we reasonably believe your use poses a risk to
              other users, us, or third parties. Upon termination, your right to use the Service ceases
              immediately. Sections 7, 8, 11, 12, 13, and 15 survive termination.
            </p>
          </section>

          <section>
            <h2>15. Governing Law and Disputes</h2>
            <p>
              These Terms are governed by the laws of the State of California, without regard to its
              conflict of law provisions. Any dispute arising from these Terms or your use of the Service
              shall be resolved by binding arbitration in San Francisco County, California, except that
              either party may seek injunctive relief in court for intellectual property disputes.
            </p>
            <p>
              You waive any right to bring claims as a class action or in a representative capacity.
            </p>
          </section>

          <section>
            <h2>16. General</h2>
            <p>
              These Terms, together with the Privacy Policy, constitute the entire agreement between you
              and Salifly regarding the Service and supersede all prior agreements. If any provision of
              these Terms is found unenforceable, the remaining provisions remain in full force. Our
              failure to enforce any right or provision is not a waiver of that right.
            </p>
            <p>
              We may assign these Terms or our rights hereunder without restriction. You may not assign
              your rights or obligations under these Terms without our prior written consent.
            </p>
          </section>

          <section>
            <h2>17. Contact</h2>
            <p>Questions about these Terms? Contact us at:</p>
            <p>
              <strong>Salifly</strong><br />
              Email: <a href="mailto:legal@salifly.com">legal@salifly.com</a><br />
              General inquiries: <a href="mailto:hello@salifly.com">hello@salifly.com</a>
            </p>
          </section>
        </div>
      </div>

      <footer className="page-footer">
        <div className="page-footer-inner">
          <div className="page-footer-row">
            <p className="page-footer-copy">© 2025 Salifly. All rights reserved.</p>
            <div className="page-footer-links">
              <button className="page-footer-link lp-footer-link-btn" onClick={() => onNavigate('privacy')}>Privacy</button>
              <button className="page-footer-link lp-footer-link-btn" onClick={() => onNavigate('terms')}>Terms</button>
              <a href="mailto:hello@salifly.com" className="page-footer-link">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
